/**
 * @typedef {Object} BusinessUnit
 * @property {string} [divisao]
 * @property {number} [id]
 * @property {string} [nome]
 */

/**
 * @typedef Perfil
 * @property {number} id
 * @property {string} especialidade
 * @property {string} senerioridade
 */

/**
 * @typedef Participant
 * @property {number} id
 * @property {number} funcionario_id
 * @property {string} funcionario
 * @property {string} nome
 * @property {string} email
 * @property {string} matricula
 * @property {string} formacao
 * @property {string} linkedin
 * @property {string} papel_projeto
 * @property {number} tipo_particpante
 * @property {Perfil} perfil
 * @property {number} budget_key
 */

/**
 * @typedef Rubrica
 * @property {number} [id]
 * @property {number} [tipo_id]
 * @property {string} [descricao]
 */

/**
 * @typedef Versao
 * @property {number} id
 * @property {string} label
 * @property {number} versao
 * @property {boolean} versao_oficial
 */

/**
 * @typedef Workflow
 * @property {number} id
 * @property {number} empresa_id
 * @property {string} nome
 * @property {number[]} revisores_tipo_id
 * @property {boolean} definir_revisores
 * @property {number} quantidade_revisores_aprovacao
 * @property {number} sla
 * @property {number} ordem
 * @property {string} criado_em
 * @property {string} modificado_em
 * @property {null} revisores
 */

/**
 * @typedef FrontendWorkflowStep
 * @property {number} id
 * @property {string} name
 * @property {number[]} requiredHierarchicalLevels
 * @property {boolean} defineReviewers
 * @property {boolean} selfReviewer
 * @property {number} maxReviewers
 * @property {number} minReviewers
 * @property {FrontendWorkflowStep} adjustmentStep
 * @property {FrontendWorkflowStep} nextStep
 * @property {FrontendWorkflowStep} reprovalStep
 * @property {(str: string) => boolean} isEquivalent
 */

/**
 * @typedef {Object} BusinessPlanFormData
 * @property {Array} [anexos]
 * @property {number} [ativo_controle]
 * @property {string} [ativo_destinado_uso]
 * @property {string} [ativo_forma_separacao]
 * @property {null} [ativo_moeda_caixa]
 * @property {number} [ativo_produto_servico_semelhante]
 * @property {string} [ativo_produto_servico_semelhante_desc]
 * @property {null} [ativo_substancia_fisica]
 * @property {number[]} [business_unit_ids]
 * @property {BusinessUnit[]} [business_units]
 * @property {string} [data_fim]
 * @property {string} [data_inicio]
 * @property {boolean} [editavel]
 * @property {string} [empresa_beneficiada]
 * @property {number} [engenharia_valor]
 * @property {number} [id]
 * @property {string} [impacto_financeiro]
 * @property {string} [iniciativa_artefato_tecnologico]
 * @property {string} [iniciativa_escopo]
 * @property {string} [iniciativa_escopo_solucao]
 * @property {string} [iniciativa_inovacoes]
 * @property {string} [iniciativa_relacoes_tecnologia]
 * @property {string} [iniciativa_riscos_barreiras]
 * @property {string} [kpi]
 * @property {number} [lider_negocio]
 * @property {number} [lider_produto]
 * @property {number} [lider_tecnologia]
 * @property {number} [mercado_comercializacao]
 * @property {string} [nome]
 * @property {Participant[]} [participantes]
 * @property {string} [potenciais_consumidores]
 * @property {string} [relacao_id_epicos_portifolios]
 * @property {Array} [responsaveis]
 * @property {null} [responsaveis_participantes_registro_horas]
 * @property {null} [responsavel_tecnico]
 * @property {null} [retorno_financeiro]
 * @property {Rubrica[]} [rubricas] rubricas de VPL vinculadas a este BP
 * @property {number} [status]
 * @property {string} [status_label]
 * @property {string} [tracking]
 * @property {string} [tracking_detalhamento]
 * @property {number} [usuario_id]
 * @property {Versao[]} [versoes]
 * @property {number} [vida_util]
 * @property {string} [vida_util_justificativa]
 * @property {Workflow | null} [workflow]
 * @property {string} [workflow_data_expiracao]
 * @property {number} [workflow_id]
 */

/**
 * @typedef {Object} TabItem
 * @property {number} [id]
 * @property {string} [label]
 * @property {number} [errors]
 * @property {boolean} [disabled]
 * @property {string} [tooltip]
 */

/**
 * @typedef {Object} Roadmap
 * @property {number} business_plan_id
 * @property {string} data_fim
 * @property {string} data_inicio
 * @property {string} descricao
 * @property {number} entregavel
 * @property {string} [finalidade]
 * @property {number} id
 * @property {string} nome
 * @property {Platform} [plataforma]
 * @property {number} [plataforma_id]
 */

/**
 * @typedef {Object} Platform
 * @property {number} id
 * @property {string} plataforma
 */

/**
 * @typedef {Object} Responsavel
 * @property {number} business_plan_id
 * @property {string} criado_em
 * @property {number} empresa_id
 * @property {number} id
 * @property {string} modificado_em
 * @property {Object} responsavel
 * @property {number} responsavel.business_unit_id
 * @property {string} responsavel.criado_em
 * @property {string} responsavel.email
 * @property {number} responsavel.empresa_id
 * @property {number} responsavel.id
 * @property {string} responsavel.modificado_em
 * @property {Object} responsavel.nivel_hierarquico
 * @property {string} responsavel.nivel_hierarquico.criado_em
 * @property {number} responsavel.nivel_hierarquico.empresa_id
 * @property {number} responsavel.nivel_hierarquico.id
 * @property {string} responsavel.nivel_hierarquico.modificado_em
 * @property {string} responsavel.nivel_hierarquico.nome
 * @property {number} responsavel.nivel_hierarquico_id
 * @property {string} responsavel.nome
 * @property {number} responsavel.usuario_id
 * @property {number} responsavel_id
 * @property {number} status
 * @property {number} workflow_id
 */

export const TabEnum = Object.freeze({
  BP: 1,
  BENEFICIO_ECONOMICO: 2,
  ROADMAP: 4,
  ALOCACAO: 6,
  OUTRAS_DESPESAS: 7,
  VPL: 8,
  MEMORIA_EVIDENCIAS: 9
});

/** @typedef {'approve' | 'change' | 'reprove'} WorkflowReviewType */

export const WorkflowReviewEnum = Object.freeze({
  APPROVE: "approve",
  CHANGE: "change",
  REPROVE: "reprove"
});

/**
 * Devolve se uma string é ou não parte do enum WorkflowRevisionEnum
 * @param {string} value 
 * @returns {boolean}
 */
export const isNotWorkflowReview = (value) => !Object.values(WorkflowReviewEnum).includes(value);

export const BusinessPlanStatus = Object.freeze({
  PREENCHIMENTO: 1,
  APROVACAO: 2,
  ANDAMENTO: 3,
  PRE_AMORTIZACAO: 4,
  AMORTIZACAO: 5,
  REPROVADO: 6,
  PREENCHIMENTO_NOVA_VERSAO: 7,
  APROVACAO_NOVA_VERSAO: 8
});
